import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import JobInput from "./components/JobInput";
import JobList from "./components/JobList";
import Dashboard from "./components/Dashboard";
import Auth from "./components/Auth";
import UserProfile from "./components/UserProfile";
import { exportToExcel } from "./utils/excelExport";
import { saveUserJobs, getUserJobs, onAuthStateChange } from "./firebase";
import {
  BarChart3,
  Briefcase,
  LayoutDashboard,
  List,
  Plus,
  Search,
  Download,
  Filter,
  X,
  TrendingUp,
  Target,
  MapPin,
  Trash2,
  Sparkles,
  LogIn,
  Zap,
} from "lucide-react";
import "./App.css";

// Demo data generation function
const getDemoJobs = () => {
  const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
  };

  const thirtyDaysAgo = daysAgo(30);
  const twentyEightDaysAgo = daysAgo(28);
  const twentyFiveDaysAgo = daysAgo(25);
  const twentyTwoDaysAgo = daysAgo(22);
  const twentyDaysAgo = daysAgo(20);
  const eighteenDaysAgo = daysAgo(18);
  const fifteenDaysAgo = daysAgo(15);
  const twelveDaysAgo = daysAgo(12);
  const tenDaysAgo = daysAgo(10);
  const eightDaysAgo = daysAgo(8);
  const sixDaysAgo = daysAgo(6);
  const fiveDaysAgo = daysAgo(5);
  const fourDaysAgo = daysAgo(4);
  const threeDaysAgo = daysAgo(3);
  const twoDaysAgo = daysAgo(2);
  const oneDayAgo = daysAgo(1);

  return [
    {
      id: crypto.randomUUID(),
      dateAdded: thirtyDaysAgo.toISOString(),
      companyName: "Google",
      companyWebsite: "https://careers.google.com",
      jobTitle: "Senior Software Engineer",
      location: "Mountain View, CA",
      workType: "Hybrid",
      employmentType: "Full-time",
      salaryRange: "$180k-$250k",
      experienceLevel: "Senior-level",
      skillsRequired: ["Python", "React", "AWS", "Kubernetes", "TypeScript"],
      jobDescription:
        "Join our team to build scalable web applications and services that impact billions of users. You'll work on cutting-edge technologies and collaborate with talented engineers.",
      postedDate: thirtyDaysAgo.toISOString().split("T")[0],
      applicationDeadline: fifteenDaysAgo.toISOString().split("T")[0],
      applicationStatus: "Interview Scheduled",
      priority: "High",
      notes: "Technical interview scheduled with hiring manager",
      dateApplied: twentyDaysAgo.toISOString(),
      tags: ["backend", "fullstack", "cloud"],
      jobUrl: "https://careers.google.com/jobs/12345",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: twentyDaysAgo.toISOString(),
      companyName: "Meta",
      companyWebsite: "https://www.metacareers.com",
      jobTitle: "Product Manager",
      location: "Menlo Park, CA",
      workType: "On-site",
      employmentType: "Full-time",
      salaryRange: "$150k-$200k",
      experienceLevel: "Mid-level",
      skillsRequired: [
        "Product Strategy",
        "Data Analysis",
        "SQL",
        "Agile",
        "Leadership",
      ],
      jobDescription:
        "Lead product development for our social platforms. Drive product strategy and work with cross-functional teams to deliver impactful features.",
      postedDate: twentyDaysAgo.toISOString().split("T")[0],
      applicationDeadline: tenDaysAgo.toISOString().split("T")[0],
      applicationStatus: "Applied",
      priority: "High",
      notes: "Applied through company website, followed up on LinkedIn",
      dateApplied: fifteenDaysAgo.toISOString(),
      tags: ["product", "strategy", "leadership"],
      jobUrl: "https://www.metacareers.com/jobs/67890",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: fifteenDaysAgo.toISOString(),
      companyName: "Amazon",
      companyWebsite: "https://www.amazon.jobs",
      jobTitle: "Data Scientist",
      location: "Seattle, WA",
      workType: "Remote",
      employmentType: "Full-time",
      salaryRange: "$140k-$180k",
      experienceLevel: "Senior-level",
      skillsRequired: [
        "Python",
        "Machine Learning",
        "SQL",
        "TensorFlow",
        "Statistics",
      ],
      jobDescription:
        "Apply machine learning techniques to solve complex business problems. Work with massive datasets and build predictive models.",
      postedDate: fifteenDaysAgo.toISOString().split("T")[0],
      applicationDeadline: fiveDaysAgo.toISOString().split("T")[0],
      applicationStatus: "Offer Received",
      priority: "High",
      notes: "Great team culture, competitive compensation package",
      dateApplied: tenDaysAgo.toISOString(),
      tags: ["ml", "data", "python", "remote"],
      jobUrl: "https://www.amazon.jobs/jobs/54321",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: tenDaysAgo.toISOString(),
      companyName: "Stripe",
      companyWebsite: "https://stripe.com/jobs",
      jobTitle: "UX Designer",
      location: "San Francisco, CA",
      workType: "Remote",
      employmentType: "Full-time",
      salaryRange: "$120k-$160k",
      experienceLevel: "Mid-level",
      skillsRequired: [
        "Figma",
        "User Research",
        "Prototyping",
        "CSS",
        "Design Systems",
      ],
      jobDescription:
        "Design beautiful and intuitive payment experiences. Work with product and engineering teams to create world-class user interfaces.",
      postedDate: tenDaysAgo.toISOString().split("T")[0],
      applicationDeadline: twoDaysAgo.toISOString().split("T")[0],
      applicationStatus: "Not Applied",
      priority: "Medium",
      notes: "Great company culture, interesting product challenges",
      dateApplied: null,
      tags: ["design", "ux", "remote", "fintech"],
      jobUrl: "https://stripe.com/jobs/98765",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: fiveDaysAgo.toISOString(),
      companyName: "Netflix",
      companyWebsite: "https://jobs.netflix.com",
      jobTitle: "DevOps Engineer",
      location: "Los Gatos, CA",
      workType: "Hybrid",
      employmentType: "Full-time",
      salaryRange: "$160k-$200k",
      experienceLevel: "Senior-level",
      skillsRequired: ["Docker", "Kubernetes", "AWS", "CI/CD", "Terraform"],
      jobDescription:
        "Build and maintain infrastructure for our streaming platform. Ensure high availability and performance of critical services.",
      postedDate: fiveDaysAgo.toISOString().split("T")[0],
      applicationDeadline: "",
      applicationStatus: "Rejected",
      priority: "Medium",
      notes:
        "Good experience, but they wanted someone with more cloud experience",
      dateApplied: threeDaysAgo.toISOString(),
      tags: ["devops", "cloud", "infrastructure"],
      jobUrl: "https://jobs.netflix.com/jobs/24680",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: twoDaysAgo.toISOString(),
      companyName: "Airbnb",
      companyWebsite: "https://careers.airbnb.com",
      jobTitle: "Frontend Developer",
      location: "San Francisco, CA",
      workType: "Remote",
      employmentType: "Full-time",
      salaryRange: "$130k-$170k",
      experienceLevel: "Mid-level",
      skillsRequired: ["React", "JavaScript", "TypeScript", "CSS", "GraphQL"],
      jobDescription:
        "Build responsive and accessible web applications for millions of users. Work on our core booking platform.",
      postedDate: twoDaysAgo.toISOString().split("T")[0],
      applicationDeadline: "",
      applicationStatus: "Not Applied",
      priority: "Low",
      notes: "Interesting travel industry, good work-life balance",
      dateApplied: null,
      tags: ["frontend", "react", "remote", "travel"],
      jobUrl: "https://careers.airbnb.com/jobs/13579",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: twentyFiveDaysAgo.toISOString(),
      companyName: "Microsoft",
      companyWebsite: "https://careers.microsoft.com",
      jobTitle: "Software Engineer II",
      location: "Redmond, WA",
      workType: "Hybrid",
      employmentType: "Full-time",
      salaryRange: "$140k-$180k",
      experienceLevel: "Entry-level",
      skillsRequired: ["C#", ".NET", "Azure", "SQL", "Git"],
      jobDescription:
        "Develop enterprise software solutions. Work on cloud-based applications and services.",
      postedDate: twentyFiveDaysAgo.toISOString().split("T")[0],
      applicationDeadline: tenDaysAgo.toISOString().split("T")[0],
      applicationStatus: "Applied",
      priority: "Medium",
      notes: "Applied through careers page, waiting for response",
      dateApplied: fifteenDaysAgo.toISOString(),
      tags: ["backend", "cloud", "enterprise"],
      jobUrl: "https://careers.microsoft.com/jobs/86420",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: eighteenDaysAgo.toISOString(),
      companyName: "Apple",
      companyWebsite: "https://jobs.apple.com",
      jobTitle: "iOS Developer",
      location: "Cupertino, CA",
      workType: "On-site",
      employmentType: "Full-time",
      salaryRange: "$150k-$200k",
      experienceLevel: "Mid-level",
      skillsRequired: ["Swift", "iOS", "Xcode", "Core Data", "UIKit"],
      jobDescription:
        "Build innovative iOS applications that delight millions of users. Work on core iOS frameworks and features.",
      postedDate: eighteenDaysAgo.toISOString().split("T")[0],
      applicationDeadline: fiveDaysAgo.toISOString().split("T")[0],
      applicationStatus: "Applied",
      priority: "High",
      notes: "Applied through referral, strong match for skills",
      dateApplied: twelveDaysAgo.toISOString(),
      tags: ["ios", "mobile", "swift"],
      jobUrl: "https://jobs.apple.com/jobs/11223",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: twelveDaysAgo.toISOString(),
      companyName: "Tesla",
      companyWebsite: "https://www.tesla.com/careers",
      jobTitle: "Full Stack Developer",
      location: "Austin, TX",
      workType: "On-site",
      employmentType: "Full-time",
      salaryRange: "$130k-$170k",
      experienceLevel: "Mid-level",
      skillsRequired: ["Python", "React", "Node.js", "MongoDB", "Docker"],
      jobDescription:
        "Build web applications for our energy and automotive products. Work on cutting-edge technology.",
      postedDate: twelveDaysAgo.toISOString().split("T")[0],
      applicationDeadline: "",
      applicationStatus: "Not Applied",
      priority: "Medium",
      notes: "Interesting company mission, innovative products",
      dateApplied: null,
      tags: ["fullstack", "energy", "automotive"],
      jobUrl: "https://www.tesla.com/careers/33445",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: eightDaysAgo.toISOString(),
      companyName: "Spotify",
      companyWebsite: "https://jobs.spotify.com",
      jobTitle: "Backend Engineer",
      location: "New York, NY",
      workType: "Remote",
      employmentType: "Full-time",
      salaryRange: "$140k-$180k",
      experienceLevel: "Senior-level",
      skillsRequired: ["Java", "Spring Boot", "Kafka", "PostgreSQL", "Redis"],
      jobDescription:
        "Build scalable backend systems for music streaming. Work on high-performance services.",
      postedDate: eightDaysAgo.toISOString().split("T")[0],
      applicationDeadline: "",
      applicationStatus: "Interview Scheduled",
      priority: "High",
      notes: "Technical interview next week with team lead",
      dateApplied: fiveDaysAgo.toISOString(),
      tags: ["backend", "music", "remote", "scala"],
      jobUrl: "https://jobs.spotify.com/jobs/55667",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: sixDaysAgo.toISOString(),
      companyName: "Slack",
      companyWebsite: "https://slack.com/careers",
      jobTitle: "Technical Writer",
      location: "San Francisco, CA",
      workType: "Remote",
      employmentType: "Full-time",
      salaryRange: "$100k-$130k",
      experienceLevel: "Mid-level",
      skillsRequired: [
        "Technical Writing",
        "API Documentation",
        "Markdown",
        "Git",
        "REST APIs",
      ],
      jobDescription:
        "Create clear and concise documentation for developers. Improve developer experience.",
      postedDate: sixDaysAgo.toISOString().split("T")[0],
      applicationDeadline: "",
      applicationStatus: "Not Applied",
      priority: "Low",
      notes: "Good fit for technical background and writing skills",
      dateApplied: null,
      tags: ["documentation", "technical", "remote"],
      jobUrl: "https://slack.com/careers/77889",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: fourDaysAgo.toISOString(),
      companyName: "GitHub",
      companyWebsite: "https://github.com/about/careers",
      jobTitle: "Site Reliability Engineer",
      location: "Remote",
      workType: "Remote",
      employmentType: "Full-time",
      salaryRange: "$150k-$200k",
      experienceLevel: "Senior-level",
      skillsRequired: ["Go", "Kubernetes", "AWS", "Monitoring", "Linux"],
      jobDescription:
        "Ensure reliability and performance of GitHub's infrastructure. Build and maintain critical systems.",
      postedDate: fourDaysAgo.toISOString().split("T")[0],
      applicationDeadline: "",
      applicationStatus: "Applied",
      priority: "High",
      notes: "Dream company, great engineering culture",
      dateApplied: twoDaysAgo.toISOString(),
      tags: ["devops", "sre", "remote", "infrastructure"],
      jobUrl: "https://github.com/about/careers/99887",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: threeDaysAgo.toISOString(),
      companyName: "Adobe",
      companyWebsite: "https://www.adobe.com/careers",
      jobTitle: "Product Designer",
      location: "San Jose, CA",
      workType: "Hybrid",
      employmentType: "Full-time",
      salaryRange: "$120k-$160k",
      experienceLevel: "Mid-level",
      skillsRequired: [
        "Adobe XD",
        "Photoshop",
        "Illustrator",
        "Prototyping",
        "Design Systems",
      ],
      jobDescription:
        "Design creative tools and experiences for millions of users. Work on flagship products.",
      postedDate: threeDaysAgo.toISOString().split("T")[0],
      applicationDeadline: "",
      applicationStatus: "Not Applied",
      priority: "Medium",
      notes: "Creative industry leader, strong design tools",
      dateApplied: null,
      tags: ["design", "creative", "ux"],
      jobUrl: "https://www.adobe.com/careers/44556",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: oneDayAgo.toISOString(),
      companyName: "Twitter",
      companyWebsite: "https://careers.twitter.com",
      jobTitle: "Machine Learning Engineer",
      location: "San Francisco, CA",
      workType: "On-site",
      employmentType: "Full-time",
      salaryRange: "$160k-$220k",
      experienceLevel: "Senior-level",
      skillsRequired: [
        "Python",
        "TensorFlow",
        "PyTorch",
        "NLP",
        "Deep Learning",
      ],
      jobDescription:
        "Build ML models for content moderation and recommendation systems. Work on cutting-edge AI.",
      postedDate: oneDayAgo.toISOString().split("T")[0],
      applicationDeadline: "",
      applicationStatus: "Not Applied",
      priority: "Medium",
      notes: "Interesting ML challenges at scale",
      dateApplied: null,
      tags: ["ml", "ai", "nlp", "deep-learning"],
      jobUrl: "https://careers.twitter.com/jobs/22334",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: twentyEightDaysAgo.toISOString(),
      companyName: "Uber",
      companyWebsite: "https://www.uber.com/careers",
      jobTitle: "Data Analyst",
      location: "Chicago, IL",
      workType: "Hybrid",
      employmentType: "Full-time",
      salaryRange: "$90k-$120k",
      experienceLevel: "Entry-level",
      skillsRequired: [
        "SQL",
        "Tableau",
        "Excel",
        "Python",
        "Data Visualization",
      ],
      jobDescription:
        "Analyze ride-sharing data to drive business decisions. Create dashboards and reports.",
      postedDate: twentyEightDaysAgo.toISOString().split("T")[0],
      applicationDeadline: fifteenDaysAgo.toISOString().split("T")[0],
      applicationStatus: "Applied",
      priority: "Low",
      notes: "Applied online, good entry-level opportunity",
      dateApplied: twentyDaysAgo.toISOString(),
      tags: ["data", "analytics", "sql"],
      jobUrl: "https://www.uber.com/careers/66778",
    },
    {
      id: crypto.randomUUID(),
      dateAdded: twentyTwoDaysAgo.toISOString(),
      companyName: "LinkedIn",
      companyWebsite: "https://www.linkedin.com/careers",
      jobTitle: "Mobile Developer",
      location: "Sunnyvale, CA",
      workType: "Hybrid",
      employmentType: "Full-time",
      salaryRange: "$140k-$180k",
      experienceLevel: "Mid-level",
      skillsRequired: [
        "React Native",
        "iOS",
        "Android",
        "TypeScript",
        "Mobile UI",
      ],
      jobDescription:
        "Build mobile applications for professional networking. Work on cross-platform solutions.",
      postedDate: twentyTwoDaysAgo.toISOString().split("T")[0],
      applicationDeadline: tenDaysAgo.toISOString().split("T")[0],
      applicationStatus: "Offer Received",
      priority: "Medium",
      notes: "Received offer after final round, reviewing compensation package",
      dateApplied: eighteenDaysAgo.toISOString(),
      tags: ["mobile", "react-native", "cross-platform"],
      jobUrl: "https://www.linkedin.com/careers/88990",
    },
  ];
};

// Navigation Tab Component
const NavTab = ({ icon: Icon, label, active, onClick, count, highlight }) => (
  <button
    onClick={onClick}
    className={`
      relative flex items-center gap-2 px-4 py-2 rounded-lg font-display font-semibold transition-colors
      ${
        active
          ? "bg-blue-500 text-white shadow-sm"
          : highlight
            ? "bg-blue-500 text-white hover:bg-blue-600"
            : "text-zinc-700 hover:bg-zinc-100"
      }
    `}
  >
    <Icon className="w-5 h-5" />
    <span>{label}</span>
    {count > 0 && (
      <span
        className={`
        px-2 py-0.5 rounded-full text-xs font-bold
        ${active ? "bg-white/20" : "bg-zinc-100 text-zinc-700"}
      `}
      >
        {count}
      </span>
    )}
    {active && (
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 rounded-t-full" />
    )}
  </button>
);

const MobileNavTab = ({ icon: Icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`
      flex flex-col items-center gap-1 px-4 py-2 rounded-lg min-w-fit
      ${active ? "bg-blue-500 text-white" : "text-zinc-700 hover:bg-zinc-100"}
    `}
  >
    <Icon className="w-5 h-5" />
    <span className="text-xs font-display font-medium">{label}</span>
  </button>
);

const APPLICATION_STATUSES = [
  "Not Applied",
  "Applied",
  "Interview Scheduled",
  "Interview Completed",
  "Offer Received",
  "Rejected",
  "Withdrawn",
];

function App() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null); // Filter jobs by status
  const [activeTab, setActiveTab] = useState("dashboard"); // NEW: Navigation state
  const [isDemoMode, setIsDemoMode] = useState(false); // Demo mode state
  const [showSignUpModal, setShowSignUpModal] = useState(false); // Sign-up prompt modal
  const [demoBannerDismissed, setDemoBannerDismissed] = useState(false); // Dismissible banner
  const [headerSearchQuery, setHeaderSearchQuery] = useState("");
  const [showHeaderSearch, setShowHeaderSearch] = useState(false);
  const headerSearchRef = useRef(null);

  // Initialize authentication and load user data
  useEffect(() => {
    // Check for demo mode first
    const demoMode = localStorage.getItem("isDemoMode") === "true";
    setIsDemoMode(demoMode);

    if (demoMode) {
      // Load demo data
      const demoJobs = localStorage.getItem("demoJobs");
      if (demoJobs) {
        setJobs(JSON.parse(demoJobs));
      } else {
        // Load fresh demo data
        setJobs(getDemoJobs());
      }
      // Set demo user
      setUser({
        uid: "demo-user",
        email: "demo@jobtracker.com",
        displayName: "Demo User",
        isDemo: true,
      });
      return;
    }

    const unsubscribe = onAuthStateChange(async (user) => {
      if (user) {
        setUser(user);
        // Load jobs from cloud
        setSyncing(true);
        try {
          const cloudJobs = await getUserJobs(user.uid);
          if (cloudJobs.length > 0) {
            setJobs(cloudJobs);
            // Also save to localStorage for offline access
            localStorage.setItem("jobTrackerJobs", JSON.stringify(cloudJobs));
          } else {
            // No cloud jobs, check localStorage
            const localJobs = localStorage.getItem("jobTrackerJobs");
            if (localJobs) {
              const parsedJobs = JSON.parse(localJobs);
              setJobs(parsedJobs);
              // Sync to cloud
              await saveUserJobs(user.uid, parsedJobs);
            }
          }
        } catch (error) {
          console.error("Error loading cloud jobs:", error);
          setError("Failed to load jobs from cloud");
          // Fallback to localStorage
          const localJobs = localStorage.getItem("jobTrackerJobs");
          if (localJobs) {
            setJobs(JSON.parse(localJobs));
          }
        } finally {
          setSyncing(false);
        }
      } else {
        setUser(null);
        // User logged out, keep local data for potential re-login
      }
    });

    return () => unsubscribe();
  }, []);

  // Save jobs to both localStorage and cloud
  useEffect(() => {
    if (isDemoMode) {
      // Save demo jobs to separate localStorage key
      localStorage.setItem("demoJobs", JSON.stringify(jobs));
      return;
    }

    // Always save to localStorage for offline access
    localStorage.setItem("jobTrackerJobs", JSON.stringify(jobs));

    // Save to cloud if user is logged in
    if (user && jobs.length > 0) {
      const saveToCloud = async () => {
        try {
          await saveUserJobs(user.uid, jobs);
        } catch (error) {
          console.error("Error saving to cloud:", error);
          setError("Failed to sync to cloud");
        }
      };

      // Debounce cloud saves
      const timeoutId = setTimeout(saveToCloud, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [jobs, user, isDemoMode]);

  // Clear header search query when leaving jobs tab
  useEffect(() => {
    if (activeTab !== "jobs") {
      setHeaderSearchQuery("");
    }
  }, [activeTab]);

  // Global keyboard shortcut: Cmd/Ctrl+K to open search, Escape to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowHeaderSearch(true);
        setTimeout(() => headerSearchRef.current?.focus(), 100);
      }
      if (e.key === "Escape" && showHeaderSearch) {
        setShowHeaderSearch(false);
        setHeaderSearchQuery("");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showHeaderSearch]);

  const addJob = useCallback((jobData) => {
    if (isDemoMode) {
      setShowSignUpModal(true);
      return;
    }
    const newJob = {
      id: crypto.randomUUID(),
      dateAdded: new Date().toISOString(),
      applicationStatus: "Not Applied",
      priority: "Medium",
      notes: "",
      dateApplied: null,
      tags: [],
      ...jobData,
    };
    setJobs((prev) => [newJob, ...prev]);
    setError(null);
  }, [isDemoMode]);

  const updateJob = useCallback((jobId, updates) => {
    setJobs((prev) =>
      prev.map((job) => (job.id === jobId ? { ...job, ...updates } : job)),
    );
  }, []);

  const clearAllJobs = useCallback(() => {
    if (isDemoMode) {
      setShowSignUpModal(true);
      return;
    }
    if (
      window.confirm(
        "Are you sure you want to delete all jobs? This action cannot be undone.",
      )
    ) {
      setJobs([]);
      localStorage.removeItem("jobTrackerJobs");
    }
  }, [isDemoMode]);

  // Navigate to jobs tab with optional status filter
  const navigateToJobs = useCallback((status = null) => {
    setStatusFilter(status);
    setActiveTab("jobs");
  }, []);

  const handleExport = useCallback(() => {
    if (isDemoMode) {
      setShowSignUpModal(true);
      return;
    }
    try {
      exportToExcel(jobs);
    } catch (err) {
      console.error("Error exporting to Excel:", err);
      setError("Failed to export to Excel");
    }
  }, [jobs, isDemoMode]);

  const handleAuthSuccess = useCallback((user) => {
    setUser(user);
  }, []);

  const handleLogout = useCallback(() => {
    if (isDemoMode) {
      // Exit demo mode
      localStorage.removeItem("isDemoMode");
      localStorage.removeItem("demoJobs");
      setIsDemoMode(false);
      setUser(null);
      setJobs([]);
    } else {
      // Normal logout
      setUser(null);
      setJobs([]);
      localStorage.removeItem("jobTrackerJobs");
    }
  }, [isDemoMode]);

  const exitDemoMode = useCallback(() => {
    localStorage.removeItem("isDemoMode");
    localStorage.removeItem("demoJobs");
    setIsDemoMode(false);
    setUser(null);
    setJobs([]);
  }, []);

  const handleImportData = useCallback((importedJobs) => {
    try {
      setJobs(importedJobs);
      setError(null);
    } catch (error) {
      setError("Failed to import data");
    }
  }, []);

  // Show auth screen if user is not logged in
  if (!user) {
    return <Auth onAuthSuccess={handleAuthSuccess} />;
  }

  return (
    <div className="bg-zinc-50 min-h-screen relative overflow-hidden">
      {/* Clean background pattern - subtle dots */}
      <div className="absolute inset-0 bg-[radial-gradient(#E4E4E7_1px,transparent_1px)] [background-size:16px_16px] opacity-30" />

      {/* Demo Mode Banner */}
      {isDemoMode && !demoBannerDismissed && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 sticky top-0 z-[60]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-4">
            <button
              onClick={() => setShowSignUpModal(true)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity min-w-0"
            >
              <Sparkles className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-800 truncate">
                You're exploring JobTracker in demo mode. Sign up to save your
                applications and unlock all features.
              </span>
            </button>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowSignUpModal(true)}
                className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors"
              >
                <LogIn className="w-3.5 h-3.5" />
                Sign Up
              </button>
              <button
                onClick={() => setDemoBannerDismissed(true)}
                className="p-1 text-amber-500 hover:text-amber-700 hover:bg-amber-100 rounded transition-colors"
                aria-label="Dismiss banner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header with Navigation */}
      <header className={`bg-white border-b border-zinc-200 sticky ${isDemoMode && !demoBannerDismissed ? "top-[41px]" : "top-0"} z-50 shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo/Brand */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-black font-display text-zinc-900">
                JobTracker
              </h1>
            </div>

            {/* Main Navigation Tabs */}
            <nav className="hidden md:flex items-center gap-1">
              <NavTab
                icon={LayoutDashboard}
                label="Dashboard"
                active={activeTab === "dashboard"}
                onClick={() => setActiveTab("dashboard")}
              />
              <NavTab
                icon={List}
                label="All Jobs"
                count={jobs.length}
                active={activeTab === "jobs"}
                onClick={() => setActiveTab("jobs")}
              />
              <NavTab
                icon={BarChart3}
                label="Analytics"
                active={activeTab === "analytics"}
                onClick={() => setActiveTab("analytics")}
              />
              <NavTab
                icon={Plus}
                label="Add Job"
                active={activeTab === "add"}
                onClick={() => {
                  if (isDemoMode) {
                    setShowSignUpModal(true);
                  } else {
                    setActiveTab("add");
                  }
                }}
                highlight
              />
            </nav>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (showHeaderSearch) {
                    setShowHeaderSearch(false);
                    setHeaderSearchQuery("");
                  } else {
                    setShowHeaderSearch(true);
                    setTimeout(() => headerSearchRef.current?.focus(), 100);
                  }
                }}
                className={`p-2 rounded-lg transition-colors ${
                  showHeaderSearch
                    ? "bg-blue-100 text-blue-600"
                    : "text-zinc-500 hover:bg-zinc-100"
                }`}
              >
                {showHeaderSearch ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
              </button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleExport}
                disabled={jobs.length === 0}
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-medium transition-colors hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                Export
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={clearAllJobs}
                disabled={jobs.length === 0}
                className="p-2 text-zinc-500 hover:bg-zinc-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-5 h-5" />
              </motion.button>
              <UserProfile
                user={user}
                onLogout={handleLogout}
                onExportData={handleExport}
                onImportData={handleImportData}
                isDemoMode={isDemoMode}
                onExitDemo={exitDemoMode}
              />
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center gap-1 pb-2 overflow-x-auto">
            <MobileNavTab
              icon={LayoutDashboard}
              label="Dashboard"
              active={activeTab === "dashboard"}
              onClick={() => setActiveTab("dashboard")}
            />
            <MobileNavTab
              icon={List}
              label="Jobs"
              active={activeTab === "jobs"}
              onClick={() => setActiveTab("jobs")}
            />
            <MobileNavTab
              icon={BarChart3}
              label="Stats"
              active={activeTab === "analytics"}
              onClick={() => setActiveTab("analytics")}
            />
            <MobileNavTab
              icon={Plus}
              label="Add"
              active={activeTab === "add"}
              onClick={() => {
                if (isDemoMode) {
                  setShowSignUpModal(true);
                } else {
                  setActiveTab("add");
                }
              }}
            />
          </div>
        </div>
      </header>

      {/* Expandable Header Search Bar */}
      <AnimatePresence>
        {showHeaderSearch && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-white border-b border-zinc-200 shadow-sm overflow-hidden relative z-40"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (headerSearchQuery.trim()) {
                    setActiveTab("jobs");
                    setShowHeaderSearch(false);
                  }
                }}
                className="flex items-center gap-3"
              >
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                  <input
                    ref={headerSearchRef}
                    type="text"
                    value={headerSearchQuery}
                    onChange={(e) => setHeaderSearchQuery(e.target.value)}
                    placeholder="Search jobs by title, company, location, skills..."
                    className="w-full pl-10 pr-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={!headerSearchQuery.trim()}
                  className="px-4 py-2.5 bg-blue-500 text-white rounded-lg font-medium text-sm hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Search
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setHeaderSearchQuery("");
                    setShowHeaderSearch(false);
                  }}
                  className="p-2.5 text-zinc-500 hover:bg-zinc-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4"
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <div className="w-5 h-5 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800 font-display">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Tab Content */}
        {activeTab === "dashboard" && (
          <Dashboard
            jobs={jobs}
            onUpdateJob={updateJob}
            onNavigateToJobs={navigateToJobs}
            onAddJob={() => {
              if (isDemoMode) {
                setShowSignUpModal(true);
              } else {
                setActiveTab("add");
              }
            }}
          />
        )}

        {activeTab === "jobs" && (
          <div className="space-y-6">
            {/* Job Input Section */}
            <JobInput
              onAddJob={addJob}
              setLoading={setLoading}
              setError={setError}
            />

            {/* Clear Filter Section */}
            {statusFilter && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-blue-50 border border-blue-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Filter className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-900">
                      Filtered by: {statusFilter}
                    </span>
                    <span className="text-blue-700">
                      (
                      {
                        jobs.filter(
                          (job) => job.applicationStatus === statusFilter,
                        ).length
                      }{" "}
                      jobs)
                    </span>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setStatusFilter(null)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium transition-colors hover:bg-blue-600 flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </motion.button>
                </div>
              </motion.div>
            )}

            <JobList
              jobs={jobs}
              onUpdateJob={updateJob}
              applicationStatuses={APPLICATION_STATUSES}
              statusFilter={statusFilter}
            />
          </div>
        )}

        {activeTab === "analytics" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg border border-zinc-200 p-8"
          >
            <div className="text-center">
              <TrendingUp className="w-16 h-16 text-blue-500 mx-auto mb-6" />
              <h2 className="text-3xl font-black font-display text-zinc-900 mb-4">
                Advanced Analytics
              </h2>
              <p className="text-zinc-600 mb-8 text-lg">
                Deep insights into your job search performance
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-zinc-50 rounded-lg border border-zinc-200">
                  <Target className="w-8 h-8 text-blue-500 mb-4" />
                  <h3 className="font-semibold text-zinc-900 mb-2">
                    Response Rate
                  </h3>
                  <p className="text-3xl font-bold text-blue-500">
                    {jobs.length > 0
                      ? Math.round(
                          (jobs.filter(
                            (job) => job.applicationStatus !== "Not Applied",
                          ).length /
                            jobs.length) *
                            100,
                        )
                      : 0}
                    %
                  </p>
                </div>
                <div className="p-6 bg-zinc-50 rounded-lg border border-zinc-200">
                  <MapPin className="w-8 h-8 text-blue-500 mb-4" />
                  <h3 className="font-semibold text-zinc-900 mb-2">
                    Top Location
                  </h3>
                  <p className="text-xl font-bold text-zinc-900">
                    {jobs.length > 0
                      ? Object.entries(
                          jobs.reduce((acc, job) => {
                            acc[job.location || "Unknown"] =
                              (acc[job.location || "Unknown"] || 0) + 1;
                            return acc;
                          }, {}),
                        ).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A"
                      : "N/A"}
                  </p>
                </div>
                <div className="p-6 bg-zinc-50 rounded-lg border border-zinc-200">
                  <TrendingUp className="w-8 h-8 text-blue-500 mb-4" />
                  <h3 className="font-semibold text-zinc-900 mb-2">
                    Avg. Applications/Week
                  </h3>
                  <p className="text-3xl font-bold text-blue-500">
                    {jobs.length > 0
                      ? (
                          jobs.length /
                          Math.max(
                            1,
                            Math.ceil(
                              (new Date() -
                                new Date(
                                  jobs[jobs.length - 1]?.dateAdded ||
                                    new Date(),
                                )) /
                                (1000 * 60 * 60 * 24 * 7),
                            ),
                          )
                        ).toFixed(1)
                      : "0"}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "add" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <JobInput
              onAddJob={addJob}
              setLoading={setLoading}
              setError={setError}
              loading={loading}
              error={error}
            />
          </motion.div>
        )}
      </main>

      {/* Demo Mode Sign-Up Prompt Modal */}
      {showSignUpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900 mb-2">
                Sign Up to Unlock Full Access
              </h2>
              <p className="text-zinc-600 mb-5">
                You're currently in demo mode. Create a free account to:
              </p>
              <ul className="text-left space-y-3 mb-6">
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-sm text-zinc-700">
                    Add and track unlimited jobs
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="text-sm text-zinc-700">
                    Use AI-powered job extraction
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Download className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="text-sm text-zinc-700">
                    Export data to Excel
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <LogIn className="w-4 h-4 text-amber-600" />
                  </div>
                  <span className="text-sm text-zinc-700">
                    Save progress across devices
                  </span>
                </li>
              </ul>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowSignUpModal(false);
                    exitDemoMode();
                  }}
                  className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  Create Free Account
                </button>
                <button
                  onClick={() => setShowSignUpModal(false)}
                  className="w-full py-3 text-zinc-600 font-medium rounded-lg hover:bg-zinc-100 transition-colors"
                >
                  Continue Exploring Demo
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default App;
